import { Button, Card, DatePicker, Form, Input, message } from "antd";
import { useEffect, useState, useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import { API } from "../../config/Api";
import { COMPRESS_IMAGE, POST } from "../../utils/apiRequest";
import ImagePicker from "../components/imagePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const EditProfile = ({ data, reload }: any) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [idProofImage, setIdProofImage] = useState(data?.photo || null);
  const [imageError, setImageError] = useState("");

  const handleImageChange = useCallback((newImage: any) => {
    setIdProofImage(newImage);
    setImageError("");
  }, []);

  const formatDateForAPI = (date: dayjs.Dayjs | null) => {
    if (!date) return null;
    return date.format("YYYY/MM/DD");
  };

  const parseDateFromAPI = (dateStr: string | null) => {
    if (!dateStr) return null;
    return dayjs(dateStr, "YYYY/MM/DD").startOf("day");
  };

  const handleSubmit = async (values: any) => {
    if (!idProofImage) {
      setImageError("Profile image is required");
      return;
    }

    setIsLoading(true);
    try {
      let imageUrl = data?.photo;
      if (idProofImage?.file) {
        try {
          const upload = await COMPRESS_IMAGE(idProofImage.file);
          if (upload?.url) {
            imageUrl = upload.url;
          } else {
            throw new Error("Image upload failed");
          }
        } catch (error) {
          message.error("Image upload failed. Please try again.");
          setIsLoading(false);
          return;
        }
      } else if (idProofImage?.url) {
        imageUrl = idProofImage.url;
      } else if (typeof idProofImage === "string") {
        imageUrl = idProofImage;
      }

      const formattedDate = values.birth_date
        ? formatDateForAPI(values.birth_date)
        : null;

      const response: any = await POST(API.USER_UPDATE, {
        id: data?.id,
        first_name: values.first_name,
        last_name: values.last_name,
        mobile_number: values.mobile_number,
        mobile_number2: values.mobile_number2,
        birth_date: formattedDate,
        place: values.place,
        email: values.email,
        photo: imageUrl,
      });

      if (response?.status) {
        message.success(response?.message || "Profile updated successfully");
        reload?.();
      } else {
        message.error(response?.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      message.error("Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        mobile_number: data.mobile_number || "",
        mobile_number2: data.mobile_number2 || "",
        birth_date: parseDateFromAPI(data.birth_date),
        place: data.place || "",
        email: data.email || "",
      });
      setIdProofImage(data.photo || null);
    }
  }, [data, form]);

  return (
    <Card className="col-12 col-lg-8">
      <h5 className="user-heading">Edit Profile</h5>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Item
              label="First Name"
              name="first_name"
              rules={[{ required: true, message: "First name is required" }]}
            >
              <Input placeholder="Enter your first name" />
            </Form.Item>

            <Form.Item
              label="Mobile Number"
              name="mobile_number"
              rules={[{ required: true, message: "Mobile number is required" }]}
            >
              <Input placeholder="Enter your mobile number" type="tel" />
            </Form.Item>

            <Form.Item
              label="Birth Date"
              name="birth_date"
              rules={[{ required: true, message: "Birth date is required" }]}
            >
              <DatePicker
                placeholder="Select your birth date"
                className="w-100"
                format="YYYY/MM/DD"
                showTime={false}
              />
            </Form.Item>

            <Form.Item
              label={"User Image"}
              validateStatus={imageError ? "error" : ""}
              help={imageError}
              required
            >
              <ImagePicker
                fileURL={
                  typeof idProofImage === "string"
                    ? idProofImage
                    : idProofImage?.url
                }
                onChange={handleImageChange}
                aspectRatio={1 / 1}
                height="30vh"
              />
              <div className="mt-2 text-secondary">
                <small>JPG or PNG, max 5MB</small>
              </div>
            </Form.Item>
          </Col>

          <Col md={6}>
            <Form.Item
              label="Last Name"
              name="last_name"
              rules={[{ required: true, message: "Last name is required" }]}
            >
              <Input placeholder="Enter your last name" />
            </Form.Item>

            <Form.Item label="Alternate Mobile Number" name="mobile_number2">
              <Input placeholder="Enter your alternate mobile number" />
            </Form.Item>

            <Form.Item label="Place" name="place">
              <Input placeholder="Enter your place" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Email is required" },
                { type: "email", message: "Enter a valid email address" },
              ]}
            >
              <Input placeholder="Enter your email" />
            </Form.Item>
            <Form.Item />
            <Form.Item />

            <Form.Item>
              <Button
                block
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Update
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default EditProfile;
